import React, { useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import Select from "react-select";

import classNames from "classnames";

import { useScheduleActionsContext } from "../contexts/ScheduleActions";

import { selectField } from "../../../../../features/global/globalSlice";
import { selectSelectedTournament } from "../../../../../features/tourneymgmt/tournamentManagementSlice";
import { useAppSelector } from "../../../../../hooks/reduxHooks";
import { IMasterTournament } from "../../../../../types/api";

const AdjustStartTimes = (): React.JSX.Element => {
    const tournament = useAppSelector(
        selectSelectedTournament,
    ) as IMasterTournament;
    const field = useAppSelector(state =>
        selectField(state, tournament.field.id),
    );

    const {
        adjustStartTimesGames,
        setAdjustStartTimesGames,
        adjustStartTimesDays,
        setAdjustStartTimesDays,
        adjustStartTimesDayToAdjust,
        setAdjustStartTimesDayToAdjust,
        adjustStartTimesField,
        setAdjustStartTimesField,
        schedule,
        adjustStartTimesAmount,
        setAdjustStartTimesAmount,
    } = useScheduleActionsContext();

    const allGames = schedule.games.flatMap(ageLevels => ageLevels.games || []);

    return (
        <React.Fragment>
            <Row className={classNames("border-top", "pt-2")}>
                <Col>
                    <h4>Adjust Game Start Times</h4>
                    <p>
                        Use this to adjust the start times of all the selected
                        games by the specified amount. First, select a field
                        which will display a list of days with games on that
                        field. Next, either select "All Games" to adjust all the
                        games on that field, or the specific games you want to
                        adjust. Lastly, set the "adjustment amount" in minutes
                        that you want to adjust the selected games by. This can
                        be a positive or negative number (to move games back to
                        an earlier start time), for example: 60 to move games
                        back by an hour or -30 to move a game up by 30 minutes .
                        This may result in "doubling-up" of games so please
                        review the{" "}
                        <Link
                            to={`/tournament-management/tournament/${tournament.id}/calendar`}
                        >
                            Calendar
                        </Link>{" "}
                        after making your changes.
                    </p>
                </Col>
            </Row>
            <Row className={classNames("border-bottom", "pb-2")}>
                <Col xs={3}>
                    <h5>Select Field:</h5>
                    <Select
                        isMulti={false}
                        isSearchable={false}
                        isClearable={false}
                        value={
                            adjustStartTimesField
                                ? {
                                      value: adjustStartTimesField,
                                      label: field?.diamonds.find(
                                          d => d.id === adjustStartTimesField,
                                      )?.name,
                                  }
                                : null
                        }
                        options={field?.diamonds.map(d => ({
                            value: d.id,
                            label: d.name,
                        }))}
                        onChange={f => {
                            if (f) {
                                setAdjustStartTimesField(f.value);
                                setAdjustStartTimesDayToAdjust(undefined);
                                setAdjustStartTimesAmount(undefined);
                                setAdjustStartTimesGames([]);
                                setAdjustStartTimesDays(() => {
                                    return Array.from(
                                        new Set(
                                            allGames
                                                .filter(
                                                    g => g.diamond === f.value,
                                                )
                                                .map(game =>
                                                    new Date(
                                                        game.date,
                                                    ).toLocaleDateString(
                                                        "en-US",
                                                        {
                                                            dateStyle: "short",
                                                        },
                                                    ),
                                                ),
                                        ),
                                    );
                                });
                            }
                        }}
                    />
                </Col>
                <Col xs={3}>
                    {adjustStartTimesDays.length !== 0 ? (
                        <React.Fragment>
                            <h5>Select Day</h5>
                            <Select
                                isMulti={false}
                                isSearchable={false}
                                isClearable={false}
                                value={
                                    adjustStartTimesDayToAdjust
                                        ? {
                                              value: adjustStartTimesDayToAdjust,
                                              label: new Date(
                                                  adjustStartTimesDayToAdjust,
                                              ).toLocaleDateString("en-US", {
                                                  weekday: "long",
                                              }),
                                          }
                                        : null
                                }
                                options={adjustStartTimesDays?.map(d => {
                                    return {
                                        value: d,
                                        label: new Date(d).toLocaleDateString(
                                            "en-US",
                                            {
                                                weekday: "long",
                                            },
                                        ),
                                    };
                                })}
                                onChange={d => {
                                    setAdjustStartTimesAmount(undefined);
                                    setAdjustStartTimesGames([]);
                                    d &&
                                        setAdjustStartTimesDayToAdjust(d.value);
                                }}
                            />
                        </React.Fragment>
                    ) : (
                        <></>
                    )}
                </Col>
                <Col xs={3}>
                    {adjustStartTimesDayToAdjust && (
                        <React.Fragment>
                            <h5>Select Game Times to Adjust</h5>
                            {allGames
                                .filter(
                                    g =>
                                        g.diamond === adjustStartTimesField &&
                                        new Date(g.date).toLocaleDateString(
                                            "en-US",
                                            { dateStyle: "short" },
                                        ) ===
                                            new Date(
                                                adjustStartTimesDayToAdjust,
                                            ).toLocaleDateString("en-US", {
                                                dateStyle: "short",
                                            }),
                                )
                                .map(
                                    g =>
                                        g && (
                                            <Form.Check
                                                inline
                                                label={new Date(
                                                    g?.date,
                                                ).toLocaleTimeString("en-US", {
                                                    timeStyle: "short",
                                                })}
                                                checked={adjustStartTimesGames.includes(
                                                    g.id,
                                                )}
                                                type="checkbox"
                                                value={g.id}
                                                key={`adjust-start-${g.id}`}
                                                id={`adjust-start-${g.id}`}
                                                onChange={e => {
                                                    const { checked, value } =
                                                        e.currentTarget;
                                                    const gameId = parseInt(
                                                        value,
                                                        10,
                                                    );

                                                    setAdjustStartTimesGames(
                                                        prev =>
                                                            checked
                                                                ? [
                                                                      ...prev,
                                                                      gameId,
                                                                  ]
                                                                : prev.filter(
                                                                      id =>
                                                                          id !==
                                                                          gameId,
                                                                  ),
                                                    );
                                                }}
                                            />
                                        ),
                                )}
                            <Form.Check
                                inline
                                label="All Games"
                                type="checkbox"
                                value="all"
                                key="all-games-adjust-starts"
                                id="all-games-adjust-starts"
                                checked={
                                    allGames.filter(
                                        g =>
                                            g.diamond ===
                                                adjustStartTimesField &&
                                            new Date(g.date).toLocaleDateString(
                                                "en-US",
                                                {
                                                    dateStyle: "short",
                                                },
                                            ) ===
                                                new Date(
                                                    adjustStartTimesDayToAdjust,
                                                ).toLocaleDateString("en-US", {
                                                    dateStyle: "short",
                                                }),
                                    ).length === adjustStartTimesGames.length
                                }
                                onChange={e => {
                                    e.currentTarget.checked
                                        ? setAdjustStartTimesGames(
                                              allGames
                                                  .filter(
                                                      g =>
                                                          g.diamond ===
                                                              adjustStartTimesField &&
                                                          new Date(
                                                              g.date,
                                                          ).toLocaleDateString(
                                                              "en-US",
                                                              {
                                                                  dateStyle:
                                                                      "short",
                                                              },
                                                          ) ===
                                                              new Date(
                                                                  adjustStartTimesDayToAdjust,
                                                              ).toLocaleDateString(
                                                                  "en-US",
                                                                  {
                                                                      dateStyle:
                                                                          "short",
                                                                  },
                                                              ),
                                                  )
                                                  .map(g => g.id),
                                          )
                                        : setAdjustStartTimesGames([]);
                                }}
                            />
                        </React.Fragment>
                    )}
                </Col>
                <Col xs={3}>
                    {adjustStartTimesGames.length !== 0 && (
                        <React.Fragment>
                            <h5>Set adjustment (in minutes)</h5>
                            <Form.Control
                                placeholder="Adjustment amount..."
                                value={adjustStartTimesAmount}
                                onChange={e =>
                                    setAdjustStartTimesAmount(
                                        e.currentTarget.value,
                                    )
                                }
                            ></Form.Control>
                        </React.Fragment>
                    )}
                </Col>
            </Row>
        </React.Fragment>
    );
};

export default AdjustStartTimes;
